// eslint-disable-next-line jira/filename-case
import React, { type ReactNode, Component, useEffect } from 'react';
import type { TrackerWithoutCallbacksProps } from '../../common/types.tsx';
import { ExperienceTracker } from '../tracker-base/index.tsx';

// eslint-disable-next-line @atlassian/eng-health/no-barrel-files/disallow-reexports, jira/export/restrict-types-re-export
export type { TrackerWithoutCallbacksProps };
type ResultSpecifierTrackerChildProps = {
	hasSucceeded?: boolean;
	hasFailed?: boolean;
	hasAborted?: boolean;
	children: ReactNode;
	onExperienceSuccess: () => void;
	onExperienceFailure: () => void;
	onExperienceAbort: () => void;
};

/**
 * A component designed to monitor the outcomes of specific operations (success, failure, abort) and execute corresponding actions. Ideal for tracking operation results and facilitating immediate response.
 */
// oxlint-disable-next-line react/prefer-function-component
export class ResultDeclaredTrackerChildOld extends Component<ResultSpecifierTrackerChildProps> {
	static defaultProps = {
		hasSucceeded: false,
		hasFailed: false,
		hasAborted: false,
		children: null,
	};

	componentDidMount(): void {
		this.postRenderChecks();
	}

	componentDidUpdate(): void {
		this.postRenderChecks();
	}

	postRenderChecks(): void {
		const {
			onExperienceSuccess,
			onExperienceFailure,
			onExperienceAbort,
			hasSucceeded,
			hasFailed,
			hasAborted,
		} = this.props;
		if (hasFailed) {
			onExperienceFailure();
		} else if (hasAborted) {
			onExperienceAbort();
		} else if (hasSucceeded) {
			onExperienceSuccess();
		}
	}

	render(): React.ReactNode {
		return this.props.children;
	}
}

export const ResultDeclaredTrackerChildNew = ({
	onExperienceSuccess,
	onExperienceFailure,
	onExperienceAbort,
	hasSucceeded = false,
	hasFailed = false,
	hasAborted = false,
	children = null,
}: ResultSpecifierTrackerChildProps):
	| string
	| number
	| boolean
	| React.ReactElement<unknown, string | React.JSXElementConstructor<unknown>>
	| Iterable<React.ReactNode>
	| React.ReactPortal
	| null => {
	// eslint-disable-next-line react-you-might-not-need-an-effect/no-manage-parent
	useEffect(() => {
		if (hasFailed) {
			onExperienceFailure();
		} else if (hasAborted) {
			onExperienceAbort();
		} else if (hasSucceeded) {
			onExperienceSuccess();
		}
	}, [
		hasSucceeded,
		hasFailed,
		hasAborted,
		onExperienceSuccess,
		onExperienceFailure,
		onExperienceAbort,
	]);

	return children;
};

export const ResultDeclaredTrackerChild: ({
	onExperienceSuccess,
	onExperienceFailure,
	onExperienceAbort,
	hasSucceeded,
	hasFailed,
	hasAborted,
	children,
}: ResultSpecifierTrackerChildProps) =>
	| string
	| number
	| boolean
	| React.ReactElement<unknown, string | React.JSXElementConstructor<unknown>>
	| Iterable<React.ReactNode>
	| React.ReactPortal
	| null = ResultDeclaredTrackerChildNew;

export const ExperienceSuccessTracker = ({
	children = null,
	failureEventAttributes = null,
	parentProviders = null,
	...props
}: TrackerWithoutCallbacksProps): React.JSX.Element =>
	__SERVER__ ? (
		<>{children}</>
	) : (
		<ExperienceTracker
			failureEventAttributes={failureEventAttributes}
			parentProviders={parentProviders}
			{...props}
		>
			{({ onExperienceSuccess, onExperienceFailure, onExperienceAbort }) => (
				<ResultDeclaredTrackerChild
					hasSucceeded
					onExperienceSuccess={onExperienceSuccess}
					onExperienceFailure={onExperienceFailure}
					onExperienceAbort={onExperienceAbort}
				>
					{children}
				</ResultDeclaredTrackerChild>
			)}
		</ExperienceTracker>
	);
export const ExperienceFailureTracker = ({
	children = null,
	failureEventAttributes = null,
	parentProviders = null,
	...props
}: TrackerWithoutCallbacksProps): React.JSX.Element =>
	__SERVER__ ? (
		<>{children}</>
	) : (
		<ExperienceTracker
			failureEventAttributes={failureEventAttributes}
			parentProviders={parentProviders}
			{...props}
		>
			{({ onExperienceSuccess, onExperienceFailure, onExperienceAbort }) => (
				<ResultDeclaredTrackerChild
					hasFailed
					onExperienceSuccess={onExperienceSuccess}
					onExperienceFailure={onExperienceFailure}
					onExperienceAbort={onExperienceAbort}
				>
					{children}
				</ResultDeclaredTrackerChild>
			)}
		</ExperienceTracker>
	);
export const ExperienceAbortTracker = ({
	children = null,
	failureEventAttributes = null,
	parentProviders = null,
	...props
}: TrackerWithoutCallbacksProps): React.JSX.Element =>
	__SERVER__ ? (
		<>{children}</>
	) : (
		<ExperienceTracker
			failureEventAttributes={failureEventAttributes}
			parentProviders={parentProviders}
			{...props}
		>
			{({ onExperienceSuccess, onExperienceFailure, onExperienceAbort }) => (
				<ResultDeclaredTrackerChild
					hasAborted
					onExperienceSuccess={onExperienceSuccess}
					onExperienceFailure={onExperienceFailure}
					onExperienceAbort={onExperienceAbort}
				>
					{children}
				</ResultDeclaredTrackerChild>
			)}
		</ExperienceTracker>
	);
export type ResultProps = {
	hasSucceeded?: boolean;
	hasFailed?: boolean;
	hasAborted?: boolean;
} & TrackerWithoutCallbacksProps;

export const ExperienceResultFromPropsTracker = ({
	hasSucceeded = false,
	hasFailed = false,
	hasAborted = false,
	children = null,
	failureEventAttributes = null,
	parentProviders = null,
	...trackerProps
}: ResultProps): React.JSX.Element =>
	__SERVER__ ? (
		<>{children}</>
	) : (
		<ExperienceTracker
			failureEventAttributes={failureEventAttributes}
			parentProviders={parentProviders}
			{...trackerProps}
		>
			{({ onExperienceSuccess, onExperienceFailure, onExperienceAbort }) => (
				<ResultDeclaredTrackerChild
					hasSucceeded={hasSucceeded}
					hasFailed={hasFailed}
					hasAborted={hasAborted}
					onExperienceSuccess={onExperienceSuccess}
					onExperienceFailure={onExperienceFailure}
					onExperienceAbort={onExperienceAbort}
				>
					{children}
				</ResultDeclaredTrackerChild>
			)}
		</ExperienceTracker>
	);
